// Live inspection of an internal tester (D120, spec 16; APS-547): what the console keeps of a live
// view's socket. The live Worker (crates/live) sends a viewer `frames` (a device's state, new log
// lines after the viewer's filter, at most one metrics sample and one wireframe), `status` (the
// tester's devices online, and the view's deadline), `result` (a command's outcome, to every
// viewer, never stored), and echoes `filter` and `renew`. All of it is the device's or the
// Worker's word, so every field is read defensively: a malformed message changes nothing.
// One socket carries every install of the tester; the view keeps each install apart.

/** Lines kept per device, newest first: the Worker's own ring (D120). */
export const LINES_KEPT = 200
/** Metric samples kept per device: about a minute at the device's 1 Hz. */
export const SAMPLES_KEPT = 60
/** A command with no result after this long has expired (the Worker drops it at 2 minutes). */
export const COMMAND_TIMEOUT_MS = 120_000
/** The Worker does not push status: the view asks this often. */
export const STATUS_EVERY_MS = 10_000
/** The close code the Worker uses when a view's ticket is past its deadline. */
export const TICKET_EXPIRED = 4001

/** The OpenTelemetry severity numbers that start each level. */
export const SEVERITY_FLOOR = {
  trace: 1,
  debug: 5,
  info: 9,
  warn: 13,
  error: 17,
  fatal: 21,
} as const
export type SeverityName = keyof typeof SEVERITY_FLOOR

export const PAUSED_REASONS = ['inactive', 'low_power', 'low_data', 'data_saver'] as const
export const ENDED_REASONS = [
  'closed',
  'stop',
  'paused',
  'until',
  'session_bytes',
  'day_bytes',
  'replaced',
] as const
/** Why an online device cannot start a session (its hello's reason). */
export const WAITING_REASONS = ['inactive', 'low_power', 'capped'] as const
export type PausedReason = (typeof PAUSED_REASONS)[number]
export type EndedReason = (typeof ENDED_REASONS)[number]
export type WaitingReason = (typeof WAITING_REASONS)[number]

export const COMMANDS = ['screenshot', 'diagnostics', 'raise_log_level'] as const
export type CommandName = (typeof COMMANDS)[number]

export interface LiveLine {
  /** The line's place in the device's stream, as this view received it: its key. */
  n: number
  /** Unix ms. */
  t: number
  severity: number
  body: string
  attributes: Record<string, unknown>
}

export interface LiveSample {
  /** Unix ms. */
  t: number
  /** Bytes. */
  memory: number | null
  /** 0 to 1, only with the SDK's battery option on (off by default on iOS, on on Android). */
  battery: number | null
  charging: boolean | null
  thermal: 'nominal' | 'fair' | 'serious' | 'critical' | null
  lowPower: boolean | null
}

export interface OnlineDevice {
  install: string
  session: string
  model: string
  os: string
  release: string
  reason: WaitingReason | null
  /** Unix ms of its last hello. */
  last: number
}

export interface LiveDevice {
  install: string
  /** Null until the device's first frames. */
  state: 'live' | 'paused' | 'ended' | null
  reason: PausedReason | EndedReason | null
  /** Newest first. */
  lines: LiveLine[]
  /** Lines the viewer's filter left out, since the view opened. */
  skipped: number
  /** Lines received, since the view opened. */
  received: number
  samples: LiveSample[]
  /** The last full replay frame (spec 10.1), as the device sent it. */
  wireframe: unknown
  /** When the Worker relayed the last frames body, Unix ms. */
  at: number | null
  wireframeAt: number | null
}

export interface CommandResult {
  install: string
  id: string
  name: CommandName
  outcome: 'done' | 'failed' | 'refused'
  at: number
  result: unknown
}

export interface LiveFilter {
  min_severity: number
  text: string
}

export interface LiveView {
  /** The socket: opening, open, closed at its ticket's deadline, or closed otherwise. */
  socket: 'connecting' | 'open' | 'expired' | 'closed'
  devices: Record<string, LiveDevice>
  /** Installs in the order the view first heard of them. */
  order: string[]
  /** The tester's devices online, from the last status; null before the first. */
  online: OnlineDevice[] | null
  /** Unix ms when the Worker closes the view unless it is renewed. */
  deadline: number | null
  /** The filter the Worker applies, as it echoed it. */
  filter: LiveFilter
  /** Command results by command id. */
  results: Record<string, CommandResult>
  /** The last renew's answer: each install's new end, and whether an ended session may restart. */
  renewed: { until: Record<string, number>; allowed: boolean } | null
  /** The Worker's last error message. */
  error: string | null
}

export type LiveEvent =
  | { type: 'connecting' }
  | { type: 'open' }
  | { type: 'close'; code: number }
  | { type: 'message'; data: unknown }

export const initialView: LiveView = {
  socket: 'connecting',
  devices: {},
  order: [],
  online: null,
  deadline: null,
  filter: { min_severity: 0, text: '' },
  results: {},
  renewed: null,
  error: null,
}

const INSTALL = /^[A-Za-z0-9_-]{1,64}$/

type Json = Record<string, unknown>
const obj = (v: unknown): Json | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : null
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null)
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null)
const oneOf = <T extends string>(list: readonly T[], v: unknown): T | null =>
  typeof v === 'string' && (list as readonly string[]).includes(v) ? (v as T) : null

function line(v: unknown): Omit<LiveLine, 'n'> | null {
  const o = obj(v)
  const t = num(o?.t)
  const severity = num(o?.severity)
  const body = str(o?.body)
  if (!o || t === null || severity === null || body === null) return null
  return { t, severity, body, attributes: obj(o.attributes) ?? {} }
}

function sample(v: unknown): LiveSample | null {
  const o = obj(v)
  const t = num(o?.t)
  if (!o || t === null) return null
  const battery = num(o['apsio.battery.level'])
  return {
    t,
    memory: num(o['apsio.memory.footprint']),
    battery: battery === null ? null : Math.min(1, Math.max(0, battery)),
    charging: bool(o['apsio.battery.charging']),
    thermal: oneOf(
      ['nominal', 'fair', 'serious', 'critical'] as const,
      o['apsio.device.thermal_state'],
    ),
    lowPower: bool(o['apsio.device.low_power']),
  }
}

function online(v: unknown): OnlineDevice | null {
  const o = obj(v)
  const install = str(o?.install)
  if (!o || !install || !INSTALL.test(install)) return null
  return {
    install,
    session: str(o.session) ?? '',
    model: str(o.model) ?? '',
    os: str(o.os) ?? '',
    release: str(o.release) ?? '',
    reason: oneOf(WAITING_REASONS, o.reason),
    last: num(o.last) ?? 0,
  }
}

function result(v: unknown): CommandResult | null {
  const o = obj(v)
  const install = str(o?.install)
  const id = str(o?.id)
  const name = oneOf(COMMANDS, o?.name)
  const outcome = oneOf(['done', 'failed', 'refused'] as const, o?.outcome)
  if (!o || !install || !id || !name || !outcome) return null
  return { install, id, name, outcome, at: num(o.at) ?? 0, result: o.result ?? null }
}

const emptyDevice = (install: string): LiveDevice => ({
  install,
  state: null,
  reason: null,
  lines: [],
  skipped: 0,
  received: 0,
  samples: [],
  wireframe: null,
  at: null,
  wireframeAt: null,
})

function withDevice(view: LiveView, install: string): LiveView {
  if (view.devices[install]) return view
  return {
    ...view,
    devices: { ...view.devices, [install]: emptyDevice(install) },
    order: [...view.order, install],
  }
}

function frames(view: LiveView, m: Json): LiveView {
  const install = str(m.install)
  const state = oneOf(['live', 'paused', 'ended'] as const, m.state)
  if (!install || !INSTALL.test(install) || !state) return view
  const reason =
    state === 'paused'
      ? oneOf(PAUSED_REASONS, m.reason)
      : state === 'ended'
        ? oneOf(ENDED_REASONS, m.reason)
        : null
  const next = withDevice(view, install)
  const d = next.devices[install] as LiveDevice
  const lines = (Array.isArray(m.lines) ? m.lines : [])
    .map(line)
    .filter((l) => l !== null)
    .map((l, i) => ({ ...l, n: d.received + i }))
  // A body's lines come oldest first; the view keeps the newest first.
  const fresh = lines.reverse()
  const s = sample(m.metrics)
  const at = num(m.at) ?? d.at
  const wireframe = obj(m.wireframe)
  return {
    ...next,
    devices: {
      ...next.devices,
      [install]: {
        ...d,
        state,
        reason,
        lines: fresh.length ? [...fresh, ...d.lines].slice(0, LINES_KEPT) : d.lines,
        skipped: d.skipped + (num(m.skipped) ?? 0),
        received: d.received + lines.length,
        samples: s ? [...d.samples, s].slice(-SAMPLES_KEPT) : d.samples,
        wireframe: wireframe ?? d.wireframe,
        wireframeAt: wireframe ? at : d.wireframeAt,
        at,
      },
    },
  }
}

/** The view after one socket event. */
export function liveReducer(view: LiveView, event: LiveEvent): LiveView {
  switch (event.type) {
    case 'connecting':
      return { ...view, socket: 'connecting', error: null }
    case 'open':
      return { ...view, socket: 'open', error: null }
    case 'close':
      return { ...view, socket: event.code === TICKET_EXPIRED ? 'expired' : 'closed' }
    case 'message': {
      const m = obj(event.data)
      switch (m?.type) {
        case 'frames':
          return frames(view, m)
        case 'status': {
          const list = (Array.isArray(m.online) ? m.online : [])
            .map(online)
            .filter((d) => d !== null)
          const known = list.reduce((v, d) => withDevice(v, d.install), view)
          return { ...known, online: list, deadline: num(m.deadline) ?? view.deadline }
        }
        case 'result': {
          const r = result(m.result)
          return r ? { ...view, results: { ...view.results, [r.id]: r } } : view
        }
        case 'filter': {
          const f = obj(m.filter)
          return f
            ? {
                ...view,
                filter: { min_severity: num(f.min_severity) ?? 0, text: str(f.text) ?? '' },
              }
            : view
        }
        case 'renew': {
          const until = Object.fromEntries(
            Object.entries(obj(m.until) ?? {}).filter(
              (e): e is [string, number] => num(e[1]) !== null,
            ),
          )
          return {
            ...view,
            deadline: num(m.deadline) ?? view.deadline,
            renewed: { until, allowed: m.allowed === true },
          }
        }
        case 'error':
          return { ...view, error: str(m.error) ?? 'error' }
        default:
          return view
      }
    }
  }
}

/** The device a view shows first: the one whose frames came last, else the first online. */
export function pickDevice(view: LiveView, chosen: string | null): string | null {
  if (chosen && view.devices[chosen]) return chosen
  const heard = view.order
    .map((i) => view.devices[i] as LiveDevice)
    .filter((d) => d.at !== null)
    .sort((a, b) => (b.at ?? 0) - (a.at ?? 0))
  return heard[0]?.install ?? view.online?.[0]?.install ?? view.order[0] ?? null
}

/** A line's level name, from its severity number. */
export function severityName(severity: number): SeverityName {
  const names = Object.entries(SEVERITY_FLOOR) as [SeverityName, number][]
  return names.reduce<SeverityName>((name, [n, floor]) => (severity >= floor ? n : name), 'trace')
}

/** A line's attributes as `key=value` pairs, values as JSON when they are not strings. */
export function attributeText(attributes: Record<string, unknown>): string {
  return Object.entries(attributes)
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(' ')
}

/** Why the view could not open: live is not set up or down, not a tester, or not allowed. */
export type LiveProblem = 'unavailable' | 'not_tester' | 'forbidden' | 'rate_limited' | 'failed'

/** Without a Retry-After, a 429 waits this long before the viewer may try again. */
export const RATE_LIMIT_WAIT_S = 30

/** What the Live tab says of the view and the device it shows. */
export type LiveStatus =
  | { kind: 'problem'; problem: LiveProblem }
  | { kind: 'connecting' }
  /** The socket closed at its ticket's deadline. */
  | { kind: 'expired' }
  /** The socket closed otherwise, and is being opened again. */
  | { kind: 'reconnecting' }
  /** No session on the device yet: none online, one that cannot start (its reason), or starting. */
  | { kind: 'waiting'; online: boolean; reason: WaitingReason | null }
  | { kind: 'live' }
  | { kind: 'paused'; reason: PausedReason | null }
  /** `renew`: the session waits for a viewer to renew before another starts (spec 16.3). */
  | { kind: 'ended'; reason: EndedReason | null; renew: boolean }

export function liveStatus(
  view: LiveView,
  problem: LiveProblem | null,
  install: string | null,
): LiveStatus {
  if (problem) return { kind: 'problem', problem }
  if (view.socket === 'expired') return { kind: 'expired' }
  if (view.socket === 'closed') return { kind: 'reconnecting' }
  if (view.socket === 'connecting') return { kind: 'connecting' }
  const device = install ? view.devices[install] : undefined
  const online = view.online?.find((d) => d.install === install)
  if (device?.state === 'live') return { kind: 'live' }
  if (device?.state === 'paused') return { kind: 'paused', reason: device.reason as PausedReason }
  if (device?.state === 'ended') {
    const reason = device.reason as EndedReason | null
    // A device online again with a reason it cannot start says so rather than the old end.
    if (online?.reason) return { kind: 'waiting', online: true, reason: online.reason }
    return { kind: 'ended', reason, renew: reason === 'until' || reason === 'session_bytes' }
  }
  return { kind: 'waiting', online: Boolean(online), reason: online?.reason ?? null }
}

/** A diagnostics command's result (spec 16.6), as the tab shows it; null where the device sent none. */
export interface Diagnostics {
  /** Bytes. */
  memory: number | null
  /** The app's own storage, bytes: its bundle, data and cache together. */
  storage: number | null
  thermal: LiveSample['thermal']
  network: string | null
  queueRecords: number | null
  /** Bytes. */
  queueBytes: number | null
}

/** Reads the apsio.diagnostics record's attributes; anything else in them is left out. */
export function diagnostics(result: unknown): Diagnostics {
  const o = obj(result) ?? {}
  const parts = ['apsio.storage.app', 'apsio.storage.data', 'apsio.storage.cache']
    .map((k) => num(o[k]))
    .filter((n) => n !== null)
  return {
    memory: num(o['apsio.memory.footprint']),
    storage: parts.length ? parts.reduce((a, b) => a + b, 0) : null,
    thermal: oneOf(
      ['nominal', 'fair', 'serious', 'critical'] as const,
      o['apsio.device.thermal_state'],
    ),
    network: str(o['network.connection.type']),
    queueRecords: num(o['apsio.queue.records']),
    queueBytes: num(o['apsio.queue.bytes']),
  }
}
